/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useLayoutEffect, useRef } from 'preact/compat';

type UseFocusableCellProps = {
  /**
   * Specifies if this cell is the one that will be focused when
   * one tabs into the grid view.
   */
  isFocusable: boolean;

  /**
   * Specifies whether or not the cell is currently focused.
   */
  isFocused: boolean;
};

/**
 * A custom hook that handles focus for a month or year cell.
 */
export const useFocusableCell = <T extends HTMLElement = HTMLElement>({
  isFocusable,
  isFocused
}: UseFocusableCellProps) => {
  const cellRef = useRef<T>(null);
  const { isTabbable } = useTabbableMode();

  // Using layout effect here to set the focus as we want to focus the element
  // before the browser has a chance to paint. This way the browser don't have to
  // repaint the focused state.
  useLayoutEffect(() => {
    if (isFocusable && isFocused) {
      cellRef.current?.focus();
    }
  }, [isFocusable, isFocused]);

  return { ref: cellRef, tabIndex: isFocusable && isTabbable ? 0 : -1 };
};
